package com.kinoshazam.history

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.remember
import androidx.navigation.NavHostController
import com.kinoshazam.model.Route
import kotlinx.coroutines.flow.combine

/**
 * Mirrors both web back stacks into the browser history.
 * Platforms without a [BrowserHistoryPort] skip this.
 *
 * Place this after the join-link handler so `/join/` is still in the address
 * on the first composition, and already consumed before the first history write.
 */
@Composable
fun BindBrowserHistory(
    navController: NavHostController,
    modalNavController: NavHostController,
) {
    val port = remember { browserHistoryPort() } ?: return
    val sync = remember(port, navController, modalNavController) {
        BrowserHistorySync(port, navController, modalNavController)
    }
    val bootJoin = remember { port.capture().isJoin }

    DisposableEffect(sync) {
        val unsubscribe = port.listen(sync::onPop)
        onDispose { unsubscribe() }
    }

    LaunchedEffect(sync) {
        sync.install(bootJoin)
        @Suppress("RestrictedApi")
        combine(
            navController.currentBackStack,
            modalNavController.currentBackStack,
            WebMainTab.current,
        ) { mainEntries, modalEntries, tab ->
            val main = mainEntries.mapNotNull { it.appRoute() }.filterNot { it is Route.Update }
            val modal = modalEntries.mapNotNull { it.appRoute() }
            if (main.isEmpty()) null else NavSnapshot(main = main, modal = modal, tab = tab)
        }.collect { snapshot ->
            if (snapshot != null) sync.onSnapshot(snapshot)
        }
    }
}

private class BrowserHistorySync(
    private val port: BrowserHistoryPort,
    private val navController: NavHostController,
    private val modalNavController: NavHostController,
) {
    private var ready = false
    private var applying = false
    private var depth = 0
    private var last: NavSnapshot = NavSnapshot.home()

    fun install(bootJoin: Boolean) {
        if (bootJoin) {
            // The join handler owns this URL. The next real screen (the room)
            // is recorded from the back-stack listener.
            last = NavSnapshot.home()
            depth = 0
            ready = true
            if (!port.capture().isJoin && currentSnapshot() == last) {
                port.replace("/", encodeHistory(last, 0))
            }
            return
        }
        val location = port.capture()
        val saved = decodeHistory(location.stateJson)
        applying = true
        if (saved != null) {
            val shown = saved.snapshot.withoutSessionScreens().preferTabFromPath(location.path)
            restore(shown)
            last = shown
            depth = saved.depth
            val path = shown.browserPath()
            val json = encodeHistory(shown, saved.depth)
            if (location.path != path || shown != saved.snapshot) {
                port.replace(path, json)
            }
        } else {
            val snapshot = (decodeBrowserPath(location.path) ?: NavSnapshot.home())
                .withoutSessionScreens()
            val trail = snapshot.linearTrail()
            restore(trail.last())
            trail.forEachIndexed { index, step ->
                val json = encodeHistory(step, index)
                val path = step.browserPath()
                if (index == 0) port.replace(path, json) else port.push(path, json)
            }
            last = trail.last()
            depth = trail.lastIndex.coerceAtLeast(0)
        }
        applying = false
        ready = true
    }

    fun onSnapshot(snapshot: NavSnapshot) {
        if (!ready || applying) return
        if (port.capture().isJoin) return
        if (snapshot == last) return
        if (canStepBack(snapshot)) {
            last = snapshot
            depth -= 1
            port.back()
            return
        }
        if (isBackNavigation(last, snapshot)) {
            last = snapshot
            port.replace(snapshot.browserPath(), encodeHistory(snapshot, depth))
            return
        }
        val replaceTab = WebMainTab.consumeReplace()
        if (snapshot.browserPath() == last.browserPath() || replaceTab) {
            last = snapshot
            port.replace(snapshot.browserPath(), encodeHistory(snapshot, depth))
            return
        }
        depth += 1
        last = snapshot
        port.push(snapshot.browserPath(), encodeHistory(snapshot, depth))
    }

    fun onPop(stateJson: String?) {
        if (!ready || applying) return
        if (port.capture().isJoin) return
        val saved = decodeHistory(stateJson)
        applying = true
        if (saved != null) {
            restore(saved.snapshot)
            last = saved.snapshot
            depth = saved.depth
        } else {
            val snapshot = (decodeBrowserPath(port.capture().path) ?: NavSnapshot.home())
                .withoutSessionScreens()
            restore(snapshot)
            last = snapshot
            depth = 0
            port.replace(snapshot.browserPath(), encodeHistory(snapshot, 0))
        }
        applying = false
    }

    private fun canStepBack(snapshot: NavSnapshot): Boolean {
        if (depth <= 0 || !isBackNavigation(last, snapshot)) return false
        return last.linearTrail().size == depth + 1 && snapshot.linearTrail().size == depth
    }

    private fun currentSnapshot(): NavSnapshot =
        restorable(navController.appRoutes(), modalNavController.appRoutes()).withoutSessionScreens()

    private fun restore(snapshot: NavSnapshot) {
        WebMainTab.apply(snapshot.tab)
        runCatching {
            navController.align(snapshot.main.ifEmpty { listOf(Route.Main) })
            modalNavController.align(snapshot.modal)
        }
    }
}
